// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { faro } from '@grafana/faro-web-sdk';
import { Ad, Address, Cart, CartItem, Money, PlaceOrderRequest, Product } from '../protos/demo';
import { IProductCart, IProductCartItem, IProductCheckout } from '../types/Cart';
import request from '../utils/Request';
import streamChat, { StreamChatCallbacks } from '../utils/streamRequest';
import SessionGateway from './Session.gateway';

const basePath = '/api';

const ApiGateway = () => ({
  getCart(currencyCode: string) {
    const { userId } = SessionGateway.getSession();
    return request<IProductCart>({
      url: `${basePath}/cart`,
      queryParams: { sessionId: userId, currencyCode },
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },
  addCartItem({ currencyCode, ...item }: CartItem & { currencyCode: string }) {
    const { userId, geography, device } = SessionGateway.getSession();
    faro.api.startUserAction('clicked-add-to-cart', {
      type: 'interaction',
      device: device,
      geography: geography
    });

    return request<Cart>({
      url: `${basePath}/cart`,
      body: { item, userId, geography },
      queryParams: { currencyCode },
      method: 'POST',
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },
  restoreCartItem({ currencyCode, ...item }: CartItem & { currencyCode: string }) {
    const { userId, geography } = SessionGateway.getSession();

    return request<Cart>({
      url: `${basePath}/cart`,
      body: { item, userId, geography },
      queryParams: { currencyCode },
      method: 'POST',
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },
  emptyCart() {
    const { userId } = SessionGateway.getSession();
    return request<undefined>({
      url: `${basePath}/cart`,
      method: 'DELETE',
      body: { userId },
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },

  getSupportedCurrencyList() {
    return request<string[]>({
      url: `${basePath}/currency`,
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },

  getShippingCost(itemList: IProductCartItem[], currencyCode: string, address: Address) {
    return request<Money>({
      url: `${basePath}/shipping`,
      queryParams: {
        itemList: JSON.stringify(itemList.map(({ productId, quantity }) => ({ productId, quantity }))),
        currencyCode,
        address: JSON.stringify(address),
      },
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },

  placeOrder({ currencyCode, ...order }: PlaceOrderRequest & { currencyCode: string }) {
    const { geography, device } = SessionGateway.getSession();
    faro.api.startUserAction('initiate-checkout', {
      type: 'interaction',
      device: device,
      geography: geography
    });

    return request<IProductCheckout>({
      url: `${basePath}/checkout`,
      method: 'POST',
      queryParams: { currencyCode },
      body: order,
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },

  listProducts(currencyCode: string) {
    const { geography, device } = SessionGateway.getSession();
    faro.api.startUserAction('view-products', {
      type: 'interaction',
      device: device,
      geography: geography
    });

    return request<Product[]>({
      url: `${basePath}/products`,
      queryParams: { currencyCode },
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },
  getProduct(productId: string, currencyCode: string) {
    return request<Product>({
      url: `${basePath}/products/${productId}`,
      queryParams: { currencyCode },
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },
  listRecommendations(productIds: string[], currencyCode: string) {
    const { userId, geography } = SessionGateway.getSession();
    return request<Product[]>({
      url: `${basePath}/recommendations`,
      queryParams: {
        productIds,
        sessionId: userId,
        currencyCode,
        geography: geography
      },
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },
  listAds(contextKeys: string[]) {
    return request<Ad[]>({
      url: `${basePath}/data`,
      queryParams: {
        contextKeys,
      },
    }).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },
  sendChatMessageStream(message: string, chatSessionId: string | undefined, callbacks: StreamChatCallbacks) {
    const { userId, geography, device } = SessionGateway.getSession();
    faro.api.startUserAction('chat-message-sent', {
      type: 'interaction',
      device: device,
      geography: geography
    });

    return streamChat(
      `${basePath}/chat`,
      { message, userId: chatSessionId || userId },
      {
        ...callbacks,
        onError: (errorMessage, code) => {
          faro.api.pushError(new Error(`chat stream error: ${errorMessage}`));
          callbacks.onError?.(errorMessage, code);
        },
      }
    ).catch((error) => {
      faro.api.pushError(error);
      return Promise.reject(error);
    });
  },
});

export default ApiGateway();
